import { observer } from "mobx-react";
import React from "react";
import RecurringFeeSection from "./RecurringFeeSection";
import PriceIntervalSection from "./PriceIntervalSection";
import { PricingPageService } from "../services";
import {
  Collapsible,
  CustomLabel,
  FlexContainer,
  InfoIcon,
  Input,
  SingleSelect,
  UtilityMethods,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import {
  DescriptionCell,
  getOptions,
  LabelCell,
  ProductDetailWrapper,
  SetupDetails,
} from "../styles";
import { PricePageRecurringInputModel, PricingPageInputModel } from "../models";
import {
  ItemIncreasePriceBy,
  ItemIncreasePriceInterval,
  RecurringBillingInterval,
  RecurringFeeInterval,
} from "../enums";
import { AccountSettingsStore } from "../store";
import { Params } from "react-router-dom";

type Props = {
  pricingPageData?: PricingPageService;
  index: number;
  params: Readonly<Params<string>>;
};
type CancellationRowProps = {
  label: string;
  value: string;
  hasError: boolean;
  onChange: (value: string, isBlur?: boolean) => void;
};

export const CancellationRow: React.FC<CancellationRowProps> = ({
  label,
  value,
  hasError,
  onChange,
}) => {
  return (
    <FlexContainer className="label-description-row">
      <LabelCell>
        <CustomLabel lineHeight="40px" fontSize="16px">
          {label}
        </CustomLabel>
        <InfoIcon />
      </LabelCell>
      <DescriptionCell>
        <FlexContainer spacing={15} fxLayout="row" className="dollar-usd-input">
          <Input
            value={value}
            type="text"
            error={hasError}
            className="styled-input number-selector"
            onChange={(e, isBlur) => onChange(e.target.value, isBlur)}
          />
        </FlexContainer>
      </DescriptionCell>
    </FlexContainer>
  );
};

interface NoticeRowProps {
  label: string;
  value: string;
  hasError: boolean;
  onChange: (value: string) => void;
}

export const NoticeRow: React.FC<NoticeRowProps> = ({
  label,
  value,
  hasError,
  onChange,
}) => {
  return (
    <FlexContainer className="label-description-row">
      <LabelCell>
        <CustomLabel lineHeight="40px" fontSize="16px">
          {label}
        </CustomLabel>
        <InfoIcon />
      </LabelCell>
      <DescriptionCell className="padding-right-0">
        <FlexContainer
          spacing={15}
          fxLayout="row"
          className="price-field number-selector-wrapper"
          alignItems="center"
        >
          <Input
            value={value}
            type="number"
            error={hasError}
            className="styled-input number-selector"
            onChange={(e) => onChange(e.target.value)}
          />
          <CustomLabel>days</CustomLabel>
        </FlexContainer>
      </DescriptionCell>
    </FlexContainer>
  );
};

@observer
class AdvancedSection extends React.Component<Props> {
  get editablePrice() {
    return this.props.pricingPageData?.prices[this.props.index];
  }
  get isOpen(): boolean {
    return this.editablePrice?.isAdvancedOpen === true;
  }

  get membershipId() {
    const split = ((this.props.params && this.props.params["*"]) || "").split(
      "/"
    );
    const id = split[split.length - 1];
    if (UtilityMethods.isValidObjectId(id)) {
      return id;
    }
    return null;
  }

  toggleOpen = () => {
    this.props.pricingPageData?.updatePrice(
      new PricingPageInputModel({
        ...this.editablePrice,
        isAdvancedOpen: !this.isOpen,
      }),
      this.props.index
    );
  };

  textField(value: string, fieldName: keyof PricePageRecurringInputModel) {
    return (
      <Input
        value={value}
        type="text"
        error={this.hasError(fieldName)}
        className="styled-input number-selector"
        onChange={(e) => this.updateValue(e.target.value, fieldName)}
      />
    );
  }

  numberField(value: string, fieldName: keyof PricePageRecurringInputModel) {
    return (
      <Input
        value={value}
        type="number"
        error={this.hasError(fieldName)}
        className="styled-input number-selector"
        onChange={(e) => this.updateValue(e.target.value, fieldName)}
      />
    );
  }

  formatAmount = (value: string): string => {
    return AccountSettingsStore.formatAmount(
      parseFloat(value.toString() || "0.00"),
      true
    );
  };

  updateValue(
    value: string,
    fieldName: keyof PricePageRecurringInputModel,
    isBlur?: boolean
  ) {
    if (!this.editablePrice?.recurringSettings) return;
    let recurrenceSettings = new PricePageRecurringInputModel({
      ...this.editablePrice.recurringSettings,
    });
    switch (fieldName) {
      case "increasePriceValue":
        if (isBlur) {
          value = this.formatAmount(value);
        }
        recurrenceSettings = new PricePageRecurringInputModel({
          ...this.editablePrice.recurringSettings,
          increasePriceValue: value,
        });

        break;
      case "increasePriceInterval":
        recurrenceSettings = new PricePageRecurringInputModel({
          ...this.editablePrice.recurringSettings,
          increasePriceInterval:
            value as keyof typeof ItemIncreasePriceInterval,
        });
        break;
      case "increasePriceBy":
        recurrenceSettings = new PricePageRecurringInputModel({
          ...this.editablePrice.recurringSettings,
          increasePriceBy: value as ItemIncreasePriceBy,
        });
        break;
      case "cancellationNoticeDays":
        recurrenceSettings = new PricePageRecurringInputModel({
          ...this.editablePrice.recurringSettings,
          cancellationNoticeDays: value,
        });
        break;
      case "cancellationFee":
        if (isBlur) {
          value = this.formatAmount(value);
        }
        recurrenceSettings = new PricePageRecurringInputModel({
          ...this.editablePrice.recurringSettings,
          cancellationFee: value,
        });
        break;
      case "cancellationMinimumDue":
        if (isBlur) {
          value = this.formatAmount(value);
        }
        recurrenceSettings = new PricePageRecurringInputModel({
          ...this.editablePrice.recurringSettings,
          cancellationMinimumDue: value,
        });
        break;
      case "fee":
        if (isBlur) {
          value = this.formatAmount(value);
        }
        recurrenceSettings = new PricePageRecurringInputModel({
          ...this.editablePrice.recurringSettings,
          fee: value.toString(),
        });
        break;
      case "feeInterval":
        recurrenceSettings = new PricePageRecurringInputModel({
          ...this.editablePrice.recurringSettings,
          feeInterval: value as keyof typeof RecurringFeeInterval,
        });
        break;
    }
    this.props.pricingPageData?.updatePrice(
      new PricingPageInputModel({
        ...this.editablePrice,
        recurringSettings: new PricePageRecurringInputModel({
          ...recurrenceSettings,
        }),
      }),
      this.props.index
    );
  }

  hasError(fieldName: keyof PricePageRecurringInputModel) {
    if (!this.editablePrice?.recurringSettings) return false;

    return (
      this.editablePrice?.recurringSettings?.errors?.find(
        (error) => error.field === fieldName
      ) !== undefined
    );
  }

  errorMessage(fieldName: keyof PricePageRecurringInputModel): string {
    if (!this.editablePrice?.recurringSettings) return "";
    const error = this.editablePrice?.recurringSettings?.errors?.find(
      (error) => error.field === fieldName
    );
    return error ? error?.message || "" : "";
  }

  singleSelect(value: string, fieldName: keyof PricePageRecurringInputModel) {
    return (
      <SingleSelect
        selectedKey={value}
        nameKeys={["label"]}
        valueKeys={["value"]}
        options={[getOptions(fieldName)]}
        onChange={(option) => this.updateValue(option.value, fieldName)}
        className=""
        hasError={this.hasError(fieldName)}
      />
    );
  }

  get disableDropdownForPriceIncrease() {
    return this.membershipId
      ? this.editablePrice?.recurringSettings?.isBillEveryGreaterThan3Months(
          this.editablePrice?.recurringSettings?.billingInterval,
          this.editablePrice?.recurringSettings?.billingIntervalCount
        )
        ? false
        : this.editablePrice?.recurringSettings?.increasePriceInterval !==
            "None"
          ? false
          : true
      : !this.editablePrice?.recurringSettings?.isBillEveryGreaterThan3Months(
          this.editablePrice?.recurringSettings?.billingInterval,
          this.editablePrice?.recurringSettings?.billingIntervalCount
        );
  }

  get disableDropdownForRecurringFee() {
    return this.membershipId
      ? this.editablePrice?.recurringSettings?.isBillEveryGreaterThan3Months(
          this.editablePrice?.recurringSettings?.billingInterval,
          this.editablePrice?.recurringSettings?.billingIntervalCount
        )
        ? false
        : this.editablePrice?.recurringSettings?.feeInterval !== "None"
          ? false
          : true
      : !this.editablePrice?.recurringSettings?.isBillEveryGreaterThan3Months(
          this.editablePrice?.recurringSettings?.billingInterval,
          this.editablePrice?.recurringSettings?.billingIntervalCount
        );
  }

  render(): React.ReactNode {
    return (
      <ProductDetailWrapper>
        <SetupDetails>
          <CustomLabel
            fontSize="22px"
            fontWeight="bold"
            lineHeight="22px"
            margin="40px 0px 0px"
            className="setup"
          >
            Advanced
            <i
              className={`fa fa-dark-grey clickable ${this.isOpen ? "fa-chevron-down" : "fa-chevron-right"}`}
              onClick={() => this.toggleOpen()}
            ></i>
          </CustomLabel>

          <Collapsible
            isOpen={this.isOpen}
            className="advanced-addition-section"
          >
            <div>
              <CustomLabel
                fontSize="17px"
                fontWeight="bold"
                lineHeight="20px"
                margin="5px 0px 35px"
              >
                {" "}
                Additional Fees
              </CustomLabel>
            </div>
            <RecurringFeeSection
              feeInterval={
                this.editablePrice?.recurringSettings?.feeInterval || ""
              }
              fee={this.editablePrice?.recurringSettings?.fee || ""}
              onFeeIntervalChange={(value) =>
                this.updateValue(value, "feeInterval")
              }
              onFeeChange={(value, isBlur) =>
                this.updateValue(value, "fee", isBlur)
              }
              fieldname="feeInterval"
              hasError={(fieldName) =>
                this.hasError(fieldName as keyof PricePageRecurringInputModel)
              }
              disableRecurringSelect={this.disableDropdownForRecurringFee}
              errorMessage={
                this.errorMessage("feeInterval") || this.errorMessage("fee")
              }
            />
            <PriceIntervalSection
              increasePriceInterval={
                this.editablePrice?.recurringSettings?.increasePriceInterval ||
                ""
              }
              increasePriceValue={
                this.editablePrice?.recurringSettings?.increasePriceValue || ""
              }
              increasePriceBy={
                this.editablePrice?.recurringSettings?.increasePriceBy ||
                "Amount"
              }
              onIncreasePriceIntervalChange={(value) =>
                this.updateValue(value, "increasePriceInterval")
              }
              onIncreasePriceValueChange={(value, isBlur) =>
                this.updateValue(value, "increasePriceValue", isBlur)
              }
              onIncreasePriceByChange={(value) =>
                this.updateValue(value, "increasePriceBy")
              }
              getOptions={(fieldName) =>
                getOptions(fieldName as keyof PricePageRecurringInputModel)
              }
              hasError={(fieldName) =>
                !!this.editablePrice?.recurringSettings?.errors?.find(
                  (error) => error.field === fieldName
                )
              }
              showMerged={true}
              disablePriceIntervalSelect={this.disableDropdownForPriceIncrease}
              errorMessage={
                this.errorMessage("increasePriceInterval") ||
                this.errorMessage("increasePriceValue")
              }
            />
            <div>
              <CustomLabel
                fontSize="17px"
                fontWeight="bold"
                lineHeight="20px"
                margin="40px 0px 0px"
                className="setup"
              >
                {" "}
                Cancellation Terms
              </CustomLabel>
            </div>
            <NoticeRow
              label="Required Notice"
              value={
                this.editablePrice?.recurringSettings?.cancellationNoticeDays ||
                ""
              }
              hasError={this.hasError("cancellationNoticeDays")}
              onChange={(value) =>
                this.updateValue(value, "cancellationNoticeDays")
              }
            />
            <CancellationRow
              label="Minimum Due"
              value={
                this.editablePrice?.recurringSettings?.cancellationMinimumDue ||
                ""
              }
              hasError={this.hasError("cancellationMinimumDue")}
              onChange={(value, isBlur) =>
                this.updateValue(value, "cancellationMinimumDue", isBlur)
              }
            />
            <CancellationRow
              label="Cancellation Fee"
              value={
                this.editablePrice?.recurringSettings?.cancellationFee || ""
              }
              hasError={this.hasError("cancellationFee")}
              onChange={(value, isBlur) =>
                this.updateValue(value, "cancellationFee", isBlur)
              }
            />
          </Collapsible>
        </SetupDetails>
      </ProductDetailWrapper>
    );
  }
}

export default withRouter(AdvancedSection);
